/**
 * ToolsMainMenu component - Level 1 mode selection
 * Four mode buttons: sketch, edit, ds, cms
 */

import { createIconButton, type IconButtonAPI } from "@UI/IconButton/index.js";
import "./ToolsMainMenu.scss";

export type ToolMode = "sketch" | "edit" | "ds" | "cms";

export interface ToolsMainMenuConfig {
  readonly onModeSelect?: (mode: ToolMode) => void;
  readonly selectedMode?: ToolMode;
}

export interface ToolsMainMenuAPI {
  readonly getElement: () => HTMLElement;
  readonly setSelectedMode: (mode: ToolMode) => void;
  readonly getSelectedMode: () => ToolMode;
  readonly destroy: () => void;
}

/**
 * Creates ToolsMainMenu component with mode selection buttons
 * @param config - Configuration for the main menu
 * @returns ToolsMainMenu API
 */
export const createToolsMainMenu = (
  config: ToolsMainMenuConfig = {}
): ToolsMainMenuAPI => {
  const { onModeSelect, selectedMode = "edit" } = config;

  let isDestroyed = false;
  let currentSelectedMode: ToolMode = selectedMode;
  let modeButtons: Map<ToolMode, IconButtonAPI> = new Map();

  // Create container element
  const container = document.createElement("div");
  container.className = "tools-main-menu";

  // Mode configuration
  const modes: ToolMode[] = ["sketch", "edit", "ds", "cms"];
  const modeIcons: Record<ToolMode, string> = {
    sketch: "modes_sketch",
    edit: "modes_edit",
    ds: "modes_ds",
    cms: "modes_cms",
  };

  /**
   * Initialize the main menu with mode buttons
   */
  const initMainMenu = (): void => {
    try {
      // Create mode buttons
      modes.forEach((mode) => {
        const buttonAPI = createIconButton({
          icon: modeIcons[mode],
          size: 44,
          iconSize: 28,
          behavior: "switch",
          transparentBg: false,
          selected: mode === currentSelectedMode,
          className: `mode-button mode-button--${mode}`,
          onClick: () => handleModeClick(mode),
        });

        modeButtons.set(mode, buttonAPI);
        container.appendChild(buttonAPI.getElement());
      });
    } catch (error) {
      console.error("Failed to create ToolsMainMenu component:", error);
      throw error;
    }
  };

  /**
   * Handle mode button click
   */
  const handleModeClick = (mode: ToolMode): void => {
    if (isDestroyed) return;

    // Always notify, even if clicking the same selected mode (enables re-entry)
    onModeSelect?.(mode);

    // Keep visual selection in sync (ensure clicked mode is selected)
    if (mode !== currentSelectedMode) {
      setSelectedMode(mode);
    }
  };

  /**
   * Set the selected mode (updates button states)
   */
  const setSelectedMode = (mode: ToolMode): void => {
    if (isDestroyed) return;

    // Update internal state
    currentSelectedMode = mode;

    // Update button states
    modeButtons.forEach((buttonAPI, buttonMode) => {
      buttonAPI.setSelected(buttonMode === mode);
    });
  };

  /**
   * Get the currently selected mode
   */
  const getSelectedMode = (): ToolMode => {
    return currentSelectedMode;
  };

  /**
   * Clean up resources
   */
  const destroy = (): void => {
    if (isDestroyed) return;

    isDestroyed = true;

    // Destroy all mode buttons
    modeButtons.forEach((buttonAPI) => {
      buttonAPI.destroy();
    });
    modeButtons.clear();

    // Remove from DOM if still connected
    if (container.parentNode) {
      container.parentNode.removeChild(container);
    }
  };

  // Initialize the component
  initMainMenu();

  return {
    getElement: () => container,
    setSelectedMode,
    getSelectedMode,
    destroy,
  };
};

/**
 * Creates ToolsMainMenu component and appends to container
 * @param container - Container element to append to
 * @param config - Configuration for the main menu
 * @returns ToolsMainMenu API
 */
export const createToolsMainMenuInContainer = (
  container: HTMLElement,
  config: ToolsMainMenuConfig = {}
): ToolsMainMenuAPI => {
  const mainMenu = createToolsMainMenu(config);
  container.appendChild(mainMenu.getElement());
  return mainMenu;
};
